import { useState, useEffect } from 'react';
import { GlassCard, GlassCardHeader, GlassCardTitle, GlassCardContent } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/lib/api';
import {
  Building2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronRight
} from 'lucide-react';

interface TenantHealth {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  status: string;
  users_count: number;
  extensions_count: number;
  calls_24h: number;
  health_score: 'healthy' | 'warning' | 'critical';
  issues: string[];
}

interface TenantListResponse {
  tenants: {
    id: string;
    name: string;
    slug: string;
    status: string;
    user_count: string;
    extension_count: string;
    calls_24h: string;
  }[];
}

function calculateHealthScore(tenant: {
  status: string;
  users_count: number;
  extensions_count: number;
  calls_24h: number;
}): { score: 'healthy' | 'warning' | 'critical'; issues: string[] } {
  const issues: string[] = [];
  
  if (tenant.status !== 'active') {
    issues.push('Tenant non attivo');
  }
  if (tenant.users_count === 0) {
    issues.push('Nessun utente configurato');
  }
  if (tenant.extensions_count === 0) {
    issues.push('Nessuna estensione');
  }
  
  if (issues.length >= 2 || tenant.status !== 'active') {
    return { score: 'critical', issues };
  }
  if (issues.length === 1) {
    return { score: 'warning', issues };
  }
  return { score: 'healthy', issues: [] };
}

export function TenantHealthWidget() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantHealth[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const response = await apiClient.get<TenantListResponse>('/master/dashboard/tenants');
      if (response.success && response.data?.tenants) {
        const healthData = response.data.tenants.map(t => {
          const parsed = {
            status: t.status,
            users_count: parseInt(t.user_count) || 0,
            extensions_count: parseInt(t.extension_count) || 0,
            calls_24h: parseInt(t.calls_24h) || 0
          };
          const { score, issues } = calculateHealthScore(parsed);
          return {
            tenant_id: t.id,
            tenant_name: t.name,
            tenant_slug: t.slug,
            status: t.status,
            users_count: parsed.users_count,
            extensions_count: parsed.extensions_count,
            calls_24h: parsed.calls_24h,
            health_score: score,
            issues
          };
        });
        setTenants(healthData);
      }
    } catch (error) {
      console.error('Error loading tenant health:', error);
    } finally {
      setLoading(false);
    }
  };

  const healthyCounts = tenants.filter(t => t.health_score === 'healthy').length;
  const warningCounts = tenants.filter(t => t.health_score === 'warning').length;
  const criticalCounts = tenants.filter(t => t.health_score === 'critical').length;

  const getHealthIcon = (score: string) => {
    switch (score) {
      case 'healthy':
        return <CheckCircle2 className="h-4 w-4 text-emerald-400" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-amber-400" />;
      case 'critical':
        return <XCircle className="h-4 w-4 text-rose-400" />;
      default:
        return null;
    }
  };

  const getHealthBadge = (score: string) => {
    switch (score) {
      case 'healthy':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Healthy</Badge>;
      case 'warning':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Warning</Badge>;
      case 'critical':
        return <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30">Critical</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <GlassCard variant="elevated" className="h-full">
        <GlassCardHeader className="p-4 pb-2">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-purple-400" />
            <GlassCardTitle className="text-base">Tenant Health</GlassCardTitle>
          </div>
        </GlassCardHeader>
        <GlassCardContent className="p-4 pt-2">
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-white/5 rounded-lg animate-pulse" />
            ))}
          </div>
        </GlassCardContent>
      </GlassCard>
    );
  }

  return (
    <GlassCard variant="elevated" className="h-full">
      <GlassCardHeader className="p-4 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-purple-400" />
            <GlassCardTitle className="text-base">Tenant Health</GlassCardTitle>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="flex items-center gap-1 text-emerald-400">
              <CheckCircle2 className="h-3 w-3" /> {healthyCounts}
            </span>
            <span className="flex items-center gap-1 text-amber-400">
              <AlertTriangle className="h-3 w-3" /> {warningCounts}
            </span>
            <span className="flex items-center gap-1 text-rose-400">
              <XCircle className="h-3 w-3" /> {criticalCounts}
            </span>
          </div>
        </div>
      </GlassCardHeader>
      <GlassCardContent className="p-4 pt-2">
        <div className="space-y-2 max-h-[280px] overflow-y-auto">
          {tenants.slice(0, 6).map((tenant) => (
            <div
              key={tenant.tenant_id}
              onClick={() => navigate(`/tenants/${tenant.tenant_id}`)}
              className="flex items-center justify-between p-3 bg-muted/50 dark:bg-white/5 border border-border dark:border-white/10 rounded-lg hover:bg-muted cursor-pointer transition-colors group"
            >
              <div className="flex items-center gap-3 min-w-0">
                {getHealthIcon(tenant.health_score)}
                <div className="min-w-0">
                  <div className="font-medium text-foreground text-sm truncate">
                    {tenant.tenant_name}
                  </div>
                  {tenant.issues.length > 0 && (
                    <div className="text-xs text-muted-foreground dark:text-muted-foreground/70 truncate">
                      {tenant.issues[0]}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getHealthBadge(tenant.health_score)}
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-muted-foreground/60 transition-colors" />
              </div>
            </div>
          ))}
        </div>
        
        {tenants.length > 6 && (
          <div className="mt-3 pt-3 border-t border-border dark:border-white/10 text-center">
            <button
              onClick={() => navigate('/tenants')}
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              Vedi tutti i {tenants.length} tenant
            </button>
          </div>
        )}
      </GlassCardContent>
    </GlassCard>
  );
}
